import Resource from 'Resources/Resource';
import EventBus from 'App/services/EventBus';
import config from 'App/babel/config';
import getAngularModule from 'App/babel/angularHelpers/getAngularModule';
import { globalTracker } from 'Helpers/Tracker';
import { STYLE } from 'Store/reducers/SystemNotificationReducer';
import RoleModel from 'App/resources/Model/Role';

/*
	Standalone replacement for the write half of Tools.Role (WEB-11254). Same shape as
	App/resources/ReportView.ts (WEB-11188) — read that file first, it explains every decision
	repeated here.

	A 1:1 port of the new/getSettings/save/delete methods of upsales/common/resources/role.js.
	getMap is deliberately NOT ported: upsales/common/services/cacheRefresher.js still injects the
	Angular factory for it, and reads cannot desync events. No Angular caller writes roles, so there
	stays exactly one writer and role.* events cannot land twice.

	Notifications go through window.NotificationService rather than a static import of
	App/babel/NotificationService, which would close a require cycle through the store.
*/

type AccesType = 0 | 1 | 2 | 3;

// The modify and delete members were spelled out twice, identically, in the ambient declaration.
type ModifyDeleteAccess = {
	Client: AccesType;
	Activity: AccesType;
	Opportunity: AccesType;
	Contact: AccesType;
	Order: AccesType;
	Document: AccesType;
	undefined: AccesType;
	UserDefObj1: AccesType;
	UserDefObj2: AccesType;
	UserDefObj3: AccesType;
	UserDefObj4: AccesType;
	Agreement: AccesType;
	SocialEvent: AccesType;
	ClientQuotas: AccesType;
};

// Moved out of the ambient Tools namespace (types/Tools.d.ts), where it was used only by the
// Tools.Role block and EditRoleModal.
export type TemplateData = {
	access: {
		Client: AccesType;
		Activity: AccesType;
		Project: AccesType;
		Report: AccesType;
		Opportunity: AccesType;
		Contact: AccesType;
		Document: AccesType;
		UserDefObjAccesType: AccesType;
		UserDefObj2: AccesType;
		UserDefObj3: AccesType;
		UserDefObj4: AccesType;
		Agreement: AccesType;
		SocialEvent: AccesType;
		ClientQuotas: AccesType;
	};
	modify: ModifyDeleteAccess;
	delete: ModifyDeleteAccess;
};

export type RoleSettings = RoleModel & { templateData: TemplateData };

export type RoleOptions = {
	skipEvent?: boolean;
	skipNotification?: boolean;
	skipErrorNotification?: boolean;
};

type RoleLike = { id?: number | null; [key: string]: any };

type SaveResponse = { data?: any; error?: any; [key: string]: any };

const EVENT_PREFIX = 'role';

/*
	Reproduction of ResourceHelper.onSave/onSaveError/onDelete/onDeleteError
	(upsales/common/resources/resourceHelper.js) with the defaults role.js set at its lines 36-44.
	Only the branches role can reach are kept: no caller passes an eventPrefix function,
	analyticsProps or notificationType. Titles are ResourceHelper's super-defaults.
*/
const notify = (title: string, body: string, isError: boolean) => {
	window.NotificationService.add({
		title,
		body,
		style: isError ? STYLE.ERROR : STYLE.SUCCESS,
		icon: isError ? 'times' : 'check'
	});
};

/*
	ResourceHelper.parseError. It read error.data.error, where `error` was either the $resource error
	RESPONSE of a failed request (so data is the body — an axios rejection carries the same body on
	response.data instead, hence the first lookup below) or the response BODY of a 200 that carried
	`error`, where data.error is practically always absent and the constant error body is used. Both
	are preserved; do not "fix" the second one into showing the server error, it would change what
	the toast says.
*/
const parseError = (error: any): string | null => {
	const parsed = (error?.response?.data ?? error?.data)?.error;

	if (parsed?.translated) {
		return parsed.translated;
	}
	if (parsed?.key === 'FailedStandardIntegrationValidation') {
		return parsed.msg;
	}
	if (parsed?.key === 'StandardIntegrationError') {
		return parsed.standardIntegrationError;
	}
	return null;
};

/*
	EventBus is a facade over the real $rootScope, so the Angular listeners still hear these.
	role.added / .updated / .deleted are load-bearing:
	pages/Admin/subPages/AdminUsersAndRoles.tsx rebuilds the role tree from them and
	components/NavBar/QuotaCharts/QuotaCharts.js re-inits on role.updated. Do not add a second
	broadcast anywhere.
*/
const broadcast = (event: string, data: unknown) => {
	if (config.ENV === 'DEV' && console && console.log) {
		console.log('broadcasting:', `${EVENT_PREFIX}.${event}`);
	}
	EventBus.broadcast(`${EVENT_PREFIX}.${event}`, data);
};

const onSave = (options: RoleOptions, data: unknown, isNew: boolean) => {
	// SegmentHelper.module has no 'role' entry, so ResourceHelper tracked without props.
	globalTracker.track(`${isNew ? 'Created' : 'Updated'} ${EVENT_PREFIX}`);

	if (!options.skipEvent) {
		broadcast(isNew ? 'added' : 'updated', data);
	}
	if (!options.skipNotification) {
		notify(isNew ? 'default.saved' : 'default.updated', isNew ? 'saved.role' : 'updated.role', false);
	}
};

// createErrorBody and updateErrorBody are the same key, so this needs no isNew branch.
const onSaveError = (options: RoleOptions, err: unknown) => {
	if (!options.skipErrorNotification) {
		notify('default.error', parseError(err) ?? 'saveError.role', true);
	}
};

const onDelete = (options: RoleOptions, data: unknown) => {
	if (!options.skipEvent) {
		broadcast('deleted', data);
	}
	if (!options.skipNotification) {
		notify('default.deleted', 'deleted.role', false);
	}
};

const onDeleteError = (options: RoleOptions, err: unknown) => {
	if (!options.skipErrorNotification) {
		notify('default.error', parseError(err) ?? 'deleteError.role', true);
	}
};

class RoleResource extends Resource {
	constructor() {
		// No notificationTitle: notifications are fired by the helpers above, not by the base class.
		super('roles');
	}

	// The factory posts/puts { role: role } rather than the bare role. Non-standard for this
	// codebase, and kept — the endpoint expects it.
	_save(role: RoleLike): Promise<SaveResponse> {
		const body = { role };
		const request = role.id
			? this._putRequest(String(role.id), body, { methodName: 'save' })
			: this._postRequest('', body, { methodName: 'save' });

		return request.then((res: { data: SaveResponse }) => res.data);
	}

	_getSettings(id: number): Promise<RoleSettings> {
		return this._getRequest(String(id), { _url: 'roleSettings', methodName: 'getSettings' }).then(
			(res: { data: RoleSettings }) => res.data
		);
	}

	_delete(id: number): Promise<any> {
		return this._deleteRequest(String(id), { methodName: 'delete' }).then((res: { data: any }) => res.data);
	}
}

const resource = new RoleResource();

const Role = {
	new: () =>
		({
			name: '',
			description: '',
			parentId: 1, // DEFAULT?
			defaultCurrency: '',
			defaultSalesboardId: null,
			templateData: {
				access: {
					Client: 1,
					Activity: 1,
					Project: 1,
					Report: 1,
					Opportunity: 1,
					Contact: 1,
					Order: 1,
					Document: 1,
					UserDefObj1: 1,
					UserDefObj2: 1,
					UserDefObj3: 1,
					UserDefObj4: 1,
					Exchange: 1,
					Agreement: 1
				},
				modify: {
					Client: 1,
					Activity: 1,
					Project: 1,
					Report: 1,
					Opportunity: 1,
					Contact: 1,
					Order: 1,
					Document: 1,
					UserDefObj1: 1,
					UserDefObj2: 1,
					UserDefObj3: 1,
					UserDefObj4: 1,
					Exchange: 1,
					Agreement: 1
				},
				delete: {
					Client: 1,
					Activity: 1,
					Project: 1,
					Report: 1,
					Opportunity: 1,
					Contact: 1,
					Order: 1,
					Document: 1,
					UserDefObj1: 1,
					UserDefObj2: 1,
					UserDefObj3: 1,
					UserDefObj4: 1,
					Exchange: 1,
					Agreement: 1
				}
			}
			// The literal is a partial role, exactly as the Angular factory built it. Typed as a
			// full one so the EditRoleModal cast that consumes it keeps working.
		} as unknown as RoleSettings),

	// Resolves the raw body, not { data } — EditRoleModal reads res.templateData and GoalsActions
	// reads settings.templateData / settings.parent off it.
	getSettings: (id: number): Promise<RoleSettings> => resource._getSettings(id),

	// Resolves the raw response envelope — EditRoleModal reads res.error — and rejects when a 200
	// carries `error`, otherwise EditRoleModal's .catch never clears `saving`.
	save: (role: RoleLike, options: RoleOptions = {}): Promise<SaveResponse> => {
		if (!role) {
			return Promise.reject('No role');
		}

		const isNew = !role.id;
		const promise = resource._save(role).then(response => {
			if (response.error) {
				return Promise.reject(response);
			}
			return response;
		});

		promise
			.then(response => {
				onSave(options, response.data, isNew);
				return response;
			})
			.catch(err => {
				onSaveError(options, err);
			});

		return promise;
	},

	delete: (role: RoleLike, options: RoleOptions = {}): Promise<any> =>
		resource
			._delete(role.id as number)
			.then(res => {
				onDelete(options, role);
				return res;
			})
			.catch(err => {
				/*
					A role that still has users answers errorCode 97, and gets a warning dialog
					instead of the normal error toast. A knowingly-retained Angular bridge — swapping
					it for the React Alert modal would change the dialog and is out of scope.
				*/
				if (err?.response?.data?.error?.errorCode === 97) {
					getAngularModule('$upModal').open('warningAlert', {
						icon: 'fa-warning',
						title: 'default.warning',
						body: 'deleteError.userInRole',
						no: 'default.ok'
					});
				} else {
					onDeleteError(options, err);
				}
			})
};

export default Role;
